import styled from 'styled-components';
import { cssVars } from '../../shared/lib/styledTheme';
import { Select as BaseSelect } from '../../shared/ui/Select';

export const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  padding: 24px 32px;
  background-color: ${cssVars.bgColor};
  overflow-y: auto;
`;

export const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

export const DashboardGreeting = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: ${cssVars.textSecondary};
  margin: 0;
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

export const SearchInput = styled.input`
  padding: 10px 16px;
  border: 1px solid ${cssVars.borderColor};
  border-radius: 8px;
  background-color: ${cssVars.bgColor};
  color: ${cssVars.textPrimary};
  font-size: 14px;
  min-width: 250px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${cssVars.primary};
    box-shadow: 0 0 0 3px ${cssVars.primary}15;
  }

  &::placeholder {
    color: ${cssVars.textTertiary};
  }
`;

export const FilterSelect = styled(BaseSelect)`
  min-width: 150px;
`;

export const StatsBar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px 20px;
  background-color: ${cssVars.bgColor};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;
`;

export const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  border-radius: 8px;
  background-color: ${cssVars.bgSecondary};
  transition: all 0.2s;

  &:hover {
    background-color: ${cssVars.hover};
    transform: translateY(-2px);
  }
`;

export const StatValue = styled.div<{ $color?: string }>`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.$color || cssVars.textPrimary};
`;

export const StatLabel = styled.div`
  font-size: 12px;
  color: ${cssVars.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
`;

export const LoadingText = styled.div`
  font-size: 16px;
  color: ${cssVars.textSecondary};
`;

export const KanbanBoard = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  padding-bottom: 16px;
  height: calc(100vh - 220px);
  overflow: hidden;

  @media (max-width: 1600px) {
    grid-template-columns: repeat(5, minmax(280px, 1fr));
    overflow-x: auto;
    
    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: ${cssVars.bgSecondary};
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${cssVars.borderColor};
      border-radius: 4px;

      &:hover {
        background: ${cssVars.primary};
      }
    }
  }
`;

export const KanbanColumn = styled.div<{ $isDraggedOver?: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: ${props => props.$isDraggedOver 
    ? `${cssVars.primary}15` 
    : cssVars.bgSecondary
  };
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  border: 2px solid ${props => props.$isDraggedOver 
    ? cssVars.primary 
    : 'transparent'
  };
  transition: all 0.2s;
  min-width: 0;
  overflow: visible;
`;

export const ColumnHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid ${cssVars.borderColor};
`;

export const ColumnTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${cssVars.textPrimary};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const ColumnCount = styled.div`
  background-color: ${cssVars.primary};
  color: ${cssVars.white};
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  min-width: 24px;
  text-align: center;
`;

export const ColumnContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  overflow-y: auto;
  overflow-x: visible;
  min-height: 0;
  padding: 2px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${cssVars.borderColor};
    border-radius: 3px;

    &:hover {
      background: ${cssVars.primary};
    }
  }
`;

export const EmptyColumn = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${cssVars.textTertiary};
  font-size: 14px;
  border: 2px dashed ${cssVars.borderColor};
  border-radius: 8px;
`;

export const TicketCard = styled.div`
  position: relative;
  background-color: ${cssVars.bgColor};
  border: 1px solid ${cssVars.borderColor};
  border-radius: 12px;
  padding: 16px;
  cursor: grab;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  user-select: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: ${cssVars.primary};
  }

  &:active {
    cursor: grabbing;
    opacity: 0.8;
  }

  &:focus {
    outline: none;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

export const CardDate = styled.div`
  font-size: 11px;
  color: ${cssVars.textTertiary};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

export const PriorityTag = styled.div<{ $color: string }>`
  background-color: ${props => props.$color}20;
  color: ${props => props.$color};
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border: 1px solid ${props => props.$color}40;
`;

export const CardTitle = styled.h4`
  font-size: 15px;
  font-weight: 600;
  color: ${cssVars.textPrimary};
  margin: 0 0 8px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const CardSubtitle = styled.div`
  font-size: 13px;
  color: ${cssVars.textSecondary};
  margin-bottom: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const CardFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${cssVars.borderColor};
`;

export const SiteBadge = styled.div<{ $site: string }>`
  background-color: ${props => props.$site === 'hudle' ? '#4CAF50' : '#2196F3'};
  color: ${cssVars.white};
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
`;

export const CategoryBadge = styled.div`
  background-color: ${cssVars.bgTertiary};
  color: ${cssVars.textSecondary};
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
`;

export const AssignedUser = styled.div`
  margin-left: auto;
  font-size: 11px;
  color: ${cssVars.textSecondary};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: '👤';
    font-size: 12px;
  }
`;

export const UnreadDot = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 8px;
  height: 8px;
  background-color: ${cssVars.primary};
  border-radius: 50%;
  box-shadow: 0 0 0 2px ${cssVars.bgColor};
`;
